import { Routes } from '@angular/router';

import { UserRoleEnum } from '@enum/role.enum';
import { ownProfileMfaGuard } from '@guards/own-profile-mfa.guard';
import { rolePermissionGuard } from '@guards/role.guard';

import { ALL_PERMISSIONS } from '@models/permission.model';

/**
 * Route hierarchy enforces the permission model:
 *
 *  /my-preferences          → any authenticated user (own profile)
 *  /users-dev/:id           → developer only
 *  /users/:id               → agency manager (and master agency manager) only
 *
 * The rolePermissionGuard checks getCurrentRole() from UserPermissionService.
 * Per-tab guards enforce that junior roles cannot reach senior routes.
 */

// ─── Shared child routes (tabs) reused across both profile contexts ─
const PROFILE_CHILD_ROUTES = (): Routes => [
  {
    path: 'personal-details',
    loadComponent: () =>
      import('@pages/user-profile/personal-details/personal-details.component').then(
        (c) => c.PersonalDetailsComponent,
      ),
    data: { layout: 'private' },
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('@pages/user-profile/contact-communication/contact-communication.component').then(
        (c) => c.ContactCommunicationComponent,
      ),
    data: { layout: 'private' },
  },
  {
    path: 'permissions',
    loadComponent: () =>
      import('@pages/user-profile/role-permissions/role-permissions.component').then(
        (c) => c.RolePermissionsComponent,
      ),
    // Agents cannot access. Master agents, agency managers, developers can.
    canActivate: [rolePermissionGuard],
    data: {
      layout: 'private',
      roles: ['developer', 'developer viewer', 'master agent', 'agency manager'],
    },
  },
  {
    path: 'account-management',
    loadComponent: () =>
      import('@pages/user-profile/password-management/password-management.component').then(
        (c) => c.PasswordManagementComponent,
      ),
    data: { layout: 'private' },
  },
  {
    path: 'mfa',
    loadComponent: () =>
      import('@pages/user-profile/mfa-settings/mfa-settings.component').then(
        (c) => c.MfaSettingsComponent,
      ),
    canActivate: [ownProfileMfaGuard],
    data: { layout: 'private' },
  },
  {
    path: 'notifications',
    loadComponent: () =>
      import('@pages/user-profile/notification-preferences/notification-preferences.component').then(
        (c) => c.NotificationPreferencesComponent,
      ),
    data: { layout: 'private' },
  },
  {
    path: '',
    redirectTo: 'personal-details',
    pathMatch: 'full',
  },
];

export const USERS_ROUTES: Routes = [
  // ── Own profile (/my-preferences) — any authenticated user ───────
  {
    path: 'my-preferences',
    loadComponent: () =>
      import('@pages/user-profile/user-profile.component').then((c) => c.UserProfileComponent),
    data: { layout: 'private' },
    children: PROFILE_CHILD_ROUTES(),
  },

  // ── Developer view (/users-dev/:id) — developer only ─────────────
  {
    path: 'users-dev',
    canActivate: [rolePermissionGuard],
    data: {
      roles: [UserRoleEnum.Developer, UserRoleEnum.DeveloperViewer, UserRoleEnum.MasterAgent],
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/users/user-directory/user-directory.component').then(
            (c) => c.UserDirectoryComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'add',
        loadComponent: () =>
          import('@pages/user-profile/user-profile.component').then((c) => c.UserProfileComponent),
        // See the note on the agency-manager `add` route below (FEC-1160).
        canActivate: [rolePermissionGuard],
        data: { layout: 'private', permissions: [ALL_PERMISSIONS.CREATE_USER] },
        children: PROFILE_CHILD_ROUTES(),
      },
      {
        path: ':id',
        loadComponent: () =>
          import('@pages/user-profile/user-profile.component').then((c) => c.UserProfileComponent),
        data: { layout: 'private' },
        children: PROFILE_CHILD_ROUTES(),
      },
    ],
  },

  // ── Agency manager view (/users/:id) — agency manager only ───────
  {
    path: 'users',
    canActivate: [rolePermissionGuard],
    data: { roles: [UserRoleEnum.AgencyManager] },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/users/user-directory/user-directory.component').then(
            (c) => c.UserDirectoryComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'add',
        loadComponent: () =>
          import('@pages/user-profile/user-profile.component').then((c) => c.UserProfileComponent),
        /* FEC-1160 QA (18 Aug): "I removed create user permission for agency
           manager and it did not work." Only `add` is gated — `:id` is the
           profile screen, which view-only roles are still meant to open. */
        canActivate: [rolePermissionGuard],
        data: { layout: 'private', permissions: [ALL_PERMISSIONS.CREATE_USER] },
        children: PROFILE_CHILD_ROUTES(),
      },
      {
        path: ':id',
        loadComponent: () =>
          import('@pages/user-profile/user-profile.component').then((c) => c.UserProfileComponent),
        data: { layout: 'private' },
        children: PROFILE_CHILD_ROUTES(),
      },
    ],
  },
];
